import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    getUserRoles,
    getUserInfo,
    getAccessToken,
    refreshAccessToken,
    clearTokens,
} from "./auth";
import { jwtDecode } from "jwt-decode";
import { BASE_URL } from "../../../config";

const VALID_OJ_VALUES = [
    "PRIJEDOR",
    "CELINAC",
    "BANJA_LUKA",
    "MRKONJIC_GRAD",
    "LAKTASI",
    "KOZARSKA_DUBICA",
    "GRADISKA",
    "NOVI_GRAD",
    "SRBAC",
    "PRNJAVOR",
    "SIPOVO",
];

const normalizeOjNameForWfs = (value) => {
    if (!value) return value;
    return value.replaceAll("_", " ");
};

const escapeCqlValue = (value) => {
    if (!value) return value;
    return value.replace(/'/g, "''");
};

const extractOjFromRole = (role, prefix) => {
    if (!role.startsWith(prefix)) return null;

    const suffix = role.slice(prefix.length);

    if (!suffix || suffix === "ALL") return null;
    if (!VALID_OJ_VALUES.includes(suffix)) return null;

    return suffix;
};

export const useAccessRights = () => {
    const [user, setUser] = useState(getUserInfo());
    const [roles, setRoles] = useState(getUserRoles());
    const [idTerenskeJedinice, setIdTerenskeJedinice] = useState(null);

    const groups = user?.groups || [];

    const ojGroups = useMemo(() => {
        return groups
            .map((g) => g.match(/OJ_([^/]+)/)?.[1])
            .filter(Boolean)
            .filter((oj) => VALID_OJ_VALUES.includes(oj));
    }, [groups]);

    const hasRole = (role) => roles.includes(role);

    const canReadAll = hasRole("EK_READ_ALL");
    const canWriteAll = hasRole("EK_WRITE_ALL");

    const readOjRoles = useMemo(() => {
        return [
            ...new Set(
                roles
                    .flatMap((role) => [
                        extractOjFromRole(role, "EK_READ_"),
                        extractOjFromRole(role, "EK_WRITE_"),
                        extractOjFromRole(role, "EK_WRITE_OH_"),
                    ])
                    .filter(Boolean)
            ),
        ];
    }, [roles]);


    const writeOjRoles = useMemo(() => {
        return [
            ...new Set(
                roles
                    .map((role) => extractOjFromRole(role, "EK_WRITE_"))
                    .filter(Boolean)
            ),
        ];
    }, [roles]);

    const writeOhOjRoles = useMemo(() => {
        return [
            ...new Set(
                roles
                    .map((role) => extractOjFromRole(role, "EK_WRITE_OH_"))
                    .filter(Boolean)
            ),
        ];
    }, [roles]);

    const canReadOJ = readOjRoles.length > 0;
    const canWriteOJ = writeOjRoles.length > 0;
    const canWriteOH = writeOhOjRoles.length > 0;

    const isAdmin =
        canWriteAll &&
        canReadAll &&
        roles.some((r) => r.startsWith("realm-management"));

    const accessProfile = useMemo(() => {
        return {
            canRead: {
                value: isAdmin || canReadAll || canWriteAll,
                oj: [],
            },
            canWrite: {
                value: isAdmin || canWriteAll,
                oj: [],
            },
            canReadOrgJedinicu: {
                value: isAdmin || canReadAll || canWriteAll || canReadOJ,
                oj: isAdmin || canReadAll || canWriteAll ? [] : readOjRoles,
            },
            canWriteOrgJedinicu: {
                value: isAdmin || canWriteAll || canWriteOJ,
                oj: isAdmin || canWriteAll ? [] : writeOjRoles,
            },
            canWriteOH: {
                value: canWriteOH,
                oj: canWriteOH ? writeOhOjRoles : [],
            },
        };
    }, [
        isAdmin,
        canReadAll,
        canWriteAll,
        canReadOJ,
        canWriteOJ,
        canWriteOH,
        readOjRoles,
        writeOjRoles,
        writeOhOjRoles,
    ]);

    useEffect(() => {
        const interval = setInterval(() => {
            const updatedUser = getUserInfo();
            const updatedRoles = getUserRoles();

            if (
                JSON.stringify(updatedUser) !== JSON.stringify(user) ||
                JSON.stringify(updatedRoles) !== JSON.stringify(roles)
            ) {
                setUser(updatedUser);
                setRoles(updatedRoles);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [user, roles]);

    useEffect(() => {
        const getTerenskaJedinicaId = async (nazivOJ) => {
            try {
                const token = getAccessToken();
                const normalizedNazivOJ = normalizeOjNameForWfs(nazivOJ);
                const escapedNazivOJ = escapeCqlValue(normalizedNazivOJ);

                const response = await axios.get(`${BASE_URL}/geoserver/elektro/ows`, {
                    params: {
                        service: "WFS",
                        version: "1.0.0",
                        request: "GetFeature",
                        typeName: "elektro:cl_terenske_jedinice",
                        outputFormat: "application/json",
                        CQL_FILTER: `Naziv='${escapedNazivOJ}'`,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const feature = response.data.features?.[0];
                return feature?.properties?.ID ?? null;
            } catch (error) {
                console.error(
                    "Greška pri dohvaćanju ID terenske jedinice:",
                    error.response ? error.response.data : error.message
                );
                return null;
            }
        };

        if (ojGroups.length > 0) {
            getTerenskaJedinicaId(ojGroups[0]).then((id) => {
                setIdTerenskeJedinice(id);
            });
        } else {
            setIdTerenskeJedinice(null);
        }
    }, [ojGroups]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const token = getAccessToken();
                if (!token) return;

                const decoded = jwtDecode(token);
                const expiresIn = decoded.exp * 1000 - Date.now();

                if (expiresIn < 2 * 60 * 1000) {
                    const newToken = await refreshAccessToken();

                    if (!newToken) {
                        console.warn(
                            "Nije moguće osvežiti token. Korisnik će biti odjavljen."
                        );
                        clearTokens();
                        window.location.reload();
                    }
                }
            } catch (error) {
                console.error("Greška pri provjeri/obnovi tokena:", error);
                clearTokens();
                window.location.reload();
            }
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    return {
        isAuthenticated: hasRole("EK_AUTHENTICATED"),
        isAdmin,
        accessProfile,
        ojGroups,
        rawRoles: roles,
        rawGroups: groups,
        idTerenskeJedinice,
        user,
    };
};